import React, { useState } from 'react'
import { FormikProps, useFormik } from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { RESET_PASSWORD } from '../../../graphql/mutation'
import { toast } from 'react-toastify'
import { GET_USER_BY_ID } from '../../../graphql/query'

const initialValues = {
  password: '',
  changepassword: ''
}
interface PasswordInputProps {
  field: string
  formik: FormikProps<any>
  label: string
  showPassword: boolean
  togglePasswordVisibility: () => void
}

const registrationSchema = Yup.object().shape({
  password: Yup.string()
    .min(10, 'Password must be at least 10 characters long')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])(?=\S{8,})/,
      'Must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character without spaces'
    ),
  changepassword: Yup.string()
    .required('Password confirmation is required')
    .when('password', {
      is: (val: string) => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf([Yup.ref('password')], "Password and Confirm Password didn't match")
    })
})

const PasswordInput: React.FC<PasswordInputProps> = ({
  field,
  formik,
  label,
  showPassword,
  togglePasswordVisibility
}) => (
  <div className='mb-10 fv-row'>
    <div className='mb-1'>
      <label className='form-label fw-bolder text-dark fs-6'>{label}</label>
      <div className='position-relative mb-3 d-flex'>
        <input
          type={showPassword ? 'text' : 'password'}
          placeholder='Password'
          autoComplete='off'
          {...formik.getFieldProps(field)}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            {
              'is-invalid': formik.touched[field] && formik.errors[field]
            },
            {
              'is-valid': formik.touched[field] && !formik.errors[field]
            }
          )}
          style={{ borderRadius: '5px 0px 0px 5px' }}
        />
        <div className='input-group-append'>
          <span
            className='form-control form-control-lg form-control-solid'
            onClick={togglePasswordVisibility}
            style={{
              cursor: 'pointer',
              borderRadius: '0px 5px 5px 0px'
            }}
          >
            {showPassword ? (
              <i className='bi bi-eye-slash text-dark'></i>
            ) : (
              <i className='bi bi-eye text-dark'></i>
            )}
          </span>
        </div>
      </div>
      {formik.touched[field] && formik.errors[field] && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span role='alert'>{formik.errors[field] as string}</span>
          </div>
        </div>
      )}
    </div>
  </div>
)

const ChangePassword: React.FC = () => {
  const params = useParams()
  const uuid: any = params.uuid
  const [loadingOl] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const navigate = useNavigate()

  const [resetPassword] = useMutation(RESET_PASSWORD, {
    context: {
      headers: {
        authorization: uuid ? `Bearer ${uuid}` : ''
      }
    }
  })

  const { data, loading, error } = useQuery(GET_USER_BY_ID, {
    context: {
      headers: {
        authorization: uuid ? `Bearer ${uuid}` : ''
      }
    }
  })

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        const response = await resetPassword({
          variables: {
            input: {
              email: data.getUserById.email,
              password: values.changepassword
            }
          }
        })

        if (response.data.resetPassword.status === 200) {
          toast.success(response.data.resetPassword.message)
          localStorage.removeItem('Email')
          navigate('/auth/login')
        } else {
          toast.error(response.data.resetPassword.message)
        }
      } catch (err) {
        console.log(err)
      } finally {
        setSubmitting(false)
      }
    }
  })

  if (loading) return <>Loading....</>

  if (error) {
    console.log(error)
    return null
  }

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_signup_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='mb-10 text-center'>
        <h1 className='text-dark mb-3'>Change Password</h1>
        <div
          className='text-gray-400 fw-bold fs-4'
          style={{
            color: 'gray',
            textDecoration: 'none',
            pointerEvents: 'none',
            cursor: 'not-allowed',
            display: 'inline-block'
          }}
        >
          Change password for{' '}
          <button type='button' className='btn primary page-link'>
            {data?.getUserById?.email}
          </button>
        </div>
      </div>

      {formik.status && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      )}

      <PasswordInput
        field='password'
        formik={formik}
        label='New Password'
        showPassword={showNewPassword}
        togglePasswordVisibility={() => setShowNewPassword(!showNewPassword)}
      />
      <div className='text-muted'>
        Use 8 or more characters with a mix of letters, numbers & symbols.
      </div>

      <PasswordInput
        field='changepassword'
        formik={formik}
        label='Confirm New Password'
        showPassword={showConfirmPassword}
        togglePasswordVisibility={() => setShowConfirmPassword(!showConfirmPassword)}
      />

      <div className='text-center'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-lg btn-primary w-100 mb-5'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loadingOl && <span className='indicator-label'>Continue</span>}
          {loadingOl && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_signup_form_cancel_button'
            className='btn btn-lg btn-light-primary w-100 mb-5'
          >
            Cancel
          </button>
        </Link>
      </div>
    </form>
  )
}

export default ChangePassword
